import {css, Global} from '@emotion/react';
import React, {KeyboardEvent, useState} from 'react';
import AvenirBook from '../Assets/fonts/Avenir-Book.ttf';
import AvenirLight from '../Assets/fonts/Avenir-Light.ttf';
import AvenirBold from '../Assets/fonts/AvenirNext-Bold.ttf';
import AvenirDemiBold from '../Assets/fonts/AvenirNext-DemiBold.ttf';

export * from '@remoov/functional';

export function withState<T, I extends {value: T; setValue: (t: T) => unknown}>(
  Component: (i: I) => JSX.Element,
  defaultValue: T,
): (i: Omit<I, 'value' | 'setValue'> & {initialValue?: T}) => JSX.Element {
  return ({initialValue, ...props}) => {
    const [value, setValue] = useState(initialValue || defaultValue);
    // @ts-ignore setValue should not be a specific type
    return Component(...{value, setValue, ...props});
  };
}
export function maskZipcode(event) {
  event.currentTarget.maxLength = 5;
  let value = event.currentTarget.value;
  value = value.replace(/\D/g, '');
  event.currentTarget.value = value;
  return event;
}
export function maskPhone(event) {
  event.currentTarget.maxLength = 10;
  let value = event.currentTarget.value;
  value = value.replace(/\D/g, '');
  value = value.replace(/(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3');
  event.currentTarget.value = value;
  return event;
}
export function unmask(event) {
  event.currentTarget.maxLength = 250;
  return event;
}
export function delegateIfEnter<R, T extends () => R>(
  handler: T,
  invalid?: boolean | null | undefined,
): (event: KeyboardEvent<unknown>) => R | false {
  return event => event.key === 'Enter' && !invalid && handler();
}
export const theme = {
  colors: {
    darkBlue: '#2E3192',
    lightBlue: '#EBEBF7',
    darkerBlue: '#22264C',
    orange: '#F7941D',
    lightOrange: '#FFD6A6',
    green: '#36B35B',
    midGray: '#6d7072',
    darkGray: '#444444',
    gray: '#808080',
    lightGray: '#E0e0e0',
    midLightGray: '#6d7072',
    red: '#E66551',
    white: '#FFFFFF',
    yellow: '#FFC438',
    reperch_primary: '#11807F',
    reperch_primary_darker: '#074441',
    reperch_primary_lighter: '#DFF2F0',
    transparent: 'transparent',
    black: '#000000',
  },
  spacings: {
    xxsmall: '8px',
    xsmall: '16px',
    small: '24px',
    medium: '32px',
    large: '40px',
    xlarge: '48px',
    xxlarge: '56px',
    huge: '80px',
  },
  font: {
    family: "'Avenir Next', Verdana, 'Helvetica Neue', sans-serif",
    familyFigtree: "'Figtree', Verdana, 'Helvetica Neue', sans-serif",
    light: 300,
    normal: 400,
    medium: 500,
    bold: 600,
    xbold: 700,
    sizes: {
      xsmall: '12px',
      small: '16px',
      halfMedium: '20px',
      medium: '24px',
      large: '32px',
      xlarge: '40px',
      xxlarge: '48px',
      huge: '56px',
    },
  },
  mediaMaxWidth: {
    small: '@media (max-width: 450px)',
    small_medium: '@media (max-width: 590px)',
    medium: '@media (max-width: 768px)',
    medium_large: '@media (max-width: 920px)',
    large: '@media (max-width: 1040px)',
    large_huge: '@media (max-width: 1170px)',
    huge: '@media (max-width: 1440px)',
  },
} as const;
export type FontSize = keyof (typeof theme)['font']['sizes'];
export type FontWeight = 'light' | 'normal' | 'medium' | 'bold' | 'xbold';
export type FontColor = keyof (typeof theme)['colors'];
export const GlobalStyles = () => (
  <Global
    styles={css`
      @font-face {
        font-family: 'Avenir Next';
        src: url(${AvenirLight}) format('truetype');
        font-weight: 300;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Avenir Next';
        src: url(${AvenirBook}) format('truetype');
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Avenir Next';
        src: url(${AvenirDemiBold}) format('truetype');
        font-weight: 600;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Avenir';
        src: url(${AvenirBold}) format('truetype');
        font-weight: 700;
        font-style: normal;
        font-display: swap;
      }
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;

        &::before,
        &::after {
          box-sizing: inherit;
        }
        #mount {
          font-family: ${theme.font.family};
        }
      }
    `}
  />
);
